import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function SalesOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [paymentUpdatingId, setPaymentUpdatingId] = useState(null);

  const canDelete = user?.role === 'admin' || user?.role === 'master_admin';
  const canMarkPayment = user?.role === 'custom_role_sales' || user?.role === 'admin' || user?.role === 'master_admin';

  const loadOrders = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.orders.getSalesOrders();
      if (Array.isArray(res.data)) setOrders(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingId(deleteConfirm._id);
    setError('');
    try {
      await apiService.orders.delete(deleteConfirm._id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete order');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMarkPaymentReceived = async (orderId) => {
    setPaymentUpdatingId(orderId);
    setError('');
    try {
      await apiService.orders.markSalesOrderPaymentReceived(orderId);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to mark payment received');
    } finally {
      setPaymentUpdatingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + date.toLocaleTimeString(undefined, { timeStyle: 'short' });
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Orders</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">All sales orders — pending, confirmed, and rejected.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && (
          <div className="py-8 text-center text-gray-500">Loading orders…</div>
        )}
        {!loading && orders.length === 0 && (
          <div className="bg-white rounded-lg p-8 text-center text-gray-600 border border-gray-200 border-l-4 border-l-green-400">
            <p className="mb-2">No orders yet.</p>
            <p className="text-sm">Orders submitted from Create order will appear here.</p>
          </div>
        )}
        {!loading && orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order) => {
              const itemCount = (order.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              return (
                <div
                  key={order._id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 border-l-4 border-l-blue-400 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-800 text-base sm:text-lg">{order.orderNo}</div>
                      <div className="text-base text-gray-500 mt-1">
                        {formatDate(order.dateTime)} · {itemCount} item(s)
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-full text-sm font-medium border ${
                          order.status === 'Received' && order.paymentReceived
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : order.status === 'Received' && !order.paymentReceived
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : order.status === 'Rejected'
                            ? 'bg-red-100 text-red-800 border-red-200'
                            : 'bg-orange-100 text-orange-800 border-orange-200'
                        }`}
                      >
                        {order.status === 'Sent to Central Kitchen'
                          ? 'Pending'
                          : order.status === 'Received' && order.paymentReceived
                          ? 'Payment received'
                          : order.status === 'Received' && !order.paymentReceived
                          ? 'Pending payment'
                          : (order.status || '—')}
                      </span>
                      {canMarkPayment && order.status === 'Received' && !order.paymentReceived && (
                        <button
                          type="button"
                          onClick={() => handleMarkPaymentReceived(order._id)}
                          disabled={paymentUpdatingId != null}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg min-h-[40px] touch-manipulation"
                        >
                          {paymentUpdatingId === order._id ? 'Updating…' : 'Confirm payment received'}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(order)}
                          disabled={deletingId != null}
                          className="text-red-600 hover:text-red-800 text-sm font-medium border border-red-200 hover:bg-red-50 px-3 py-2 rounded min-h-[40px] touch-manipulation disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {(order.customerName || order.customerPhone || order.readyByTime) && (
                    <div className="text-base sm:text-lg text-gray-700 mt-2 font-medium">
                      {order.customerName && (
                        <span className="block sm:inline">Customer: <span className="text-gray-900">{order.customerName}</span></span>
                      )}
                      {order.customerPhone && (
                        <span className="block sm:inline mt-0.5 sm:mt-0 sm:ml-4">Phone: <span className="text-gray-900">{order.customerPhone}</span></span>
                      )}
                      {order.readyByTime && (
                        <span className="block sm:inline mt-0.5 sm:mt-0 sm:ml-4">
                          Ready by: {formatDate(order.readyByTime)}
                        </span>
                      )}
                    </div>
                  )}
                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="space-y-2 text-base text-gray-700">
                        {order.items.map((item, idx) => {
                          const qty = Number(item.orderQty) || 0;
                          const unitPrice = Number(item.unitPrice) || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <li key={idx} className="flex justify-between gap-3">
                              <span>
                                {item.itemName} × {qty}
                              </span>
                              <span className="font-medium tabular-nums whitespace-nowrap">
                                {unitPrice > 0 ? `SAR ${lineTotal.toFixed(2)}` : '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {order.totalAmount != null && Number(order.totalAmount) > 0 && (
                        <div className="flex justify-between text-base font-semibold text-gray-800 mt-3 pt-2 border-t border-gray-100">
                          <span>Total</span>
                          <span className="tabular-nums">SAR {Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {deleteConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={() => !deletingId && setDeleteConfirm(null)}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-sm p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-gray-800 mb-2">Delete order?</h2>
              <p className="text-gray-600 text-sm mb-4">
                &quot;{deleteConfirm.orderNo}&quot; will be removed. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deletingId != null}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                >
                  {deletingId ? 'Deleting…' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => !deletingId && setDeleteConfirm(null)}
                  disabled={deletingId != null}
                  className="py-2 px-4 border rounded-lg text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
